// Pinia store for the P2-25 command channel (docs/design/P2-25-command-channel-contract.md
// §5). Holds bookkeeping the poller (`../lib/command-poll.ts`) and consuming UI need:
//   - last-poll timestamp + connection/backoff state, for a "syncing…" indicator,
//   - the latest `JOB_STATUS` per `jobId`, deduped so a stale/out-of-order redelivery
//     (at-least-once delivery, §3.3) never regresses a terminal state back to an earlier one.
// This store does NOT own the poll loop itself (that's `command-poll.ts`, deliberately
// framework-agnostic/unit-testable without Pinia) — it's the piece of state the poller
// writes into and views read from.
import { defineStore } from 'pinia';
import type { z } from 'zod';
import type { jobStatusPayloadSchema } from '@eskayscience/shared/dto';

export type JobStatusPayload = z.infer<typeof jobStatusPayloadSchema>;

export type ConnectionState = 'idle' | 'polling' | 'backoff' | 'stopped';

/** Statuses in terminal-rank order — a redelivered update whose status ranks BEFORE the
 * one already stored for the same `jobId` is a stale/out-of-order echo and must be ignored
 * (design §5.4: "Ignore an out-of-order update whose status is terminal-then-earlier"). */
const TERMINAL_RANK = 2;
const JOB_STATUS_RANK: Record<JobStatusPayload['status'], number> = {
  QUEUED: 0,
  RUNNING: 1,
  SUCCEEDED: TERMINAL_RANK,
  FAILED: TERMINAL_RANK,
};

export const useCommandsStore = defineStore('commands', {
  state: () => ({
    connectionState: 'idle' as ConnectionState,
    lastPollAt: null as string | null,
    lastError: null as string | null,
    consecutiveFailures: 0,
    /** Keyed by `jobId` — latest known status for each in-flight/finished job. */
    jobs: {} as Record<string, JobStatusPayload>,
  }),
  getters: {
    job:
      (state) =>
      (jobId: string): JobStatusPayload | undefined =>
        state.jobs[jobId],
  },
  actions: {
    setConnectionState(state: ConnectionState): void {
      this.connectionState = state;
    },

    recordPollSuccess(): void {
      this.lastPollAt = new Date().toISOString();
      this.lastError = null;
      this.consecutiveFailures = 0;
    },

    recordPollFailure(message: string): void {
      this.lastError = message;
      this.consecutiveFailures += 1;
    },

    /** Upsert a `JOB_STATUS` payload, deduped by `(jobId, status)` and guarded against
     * out-of-order terminal-then-earlier redelivery. Returns `true` if the store actually
     * changed (a genuinely new/advancing update), `false` if it was dropped as stale/dupe —
     * callers (e.g. UI toasts) can use this to avoid re-notifying on a redelivered dupe. */
    upsertJobStatus(payload: JobStatusPayload): boolean {
      const existing = this.jobs[payload.jobId];
      if (existing) {
        if (existing.status === payload.status) return false; // exact dupe, at-least-once redelivery
        const existingRank = JOB_STATUS_RANK[existing.status];
        const incomingRank = JOB_STATUS_RANK[payload.status];
        // A job is done once it reaches ANY terminal status — a job cannot legitimately
        // complete twice, so once terminal, lock it: reject both a strictly-earlier
        // redelivery AND a different terminal status (SUCCEEDED <-> FAILED can never be a
        // genuine transition, only a redelivery/reordering artifact).
        if (
          incomingRank < existingRank ||
          (existingRank === TERMINAL_RANK && incomingRank === TERMINAL_RANK)
        ) {
          return false;
        }
      }
      this.jobs[payload.jobId] = payload;
      return true;
    },

    reset(): void {
      this.connectionState = 'idle';
      this.lastPollAt = null;
      this.lastError = null;
      this.consecutiveFailures = 0;
      this.jobs = {};
    },
  },
});
